import { useState, useEffect } from 'react';
import DashboardOverview from './components/dashboard/DashboardOverview';
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import { useStore } from './store/useStore';
import { auth } from './lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { WifiOff } from 'lucide-react';
import Toaster from "@/components/ui/custom/Toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { Routes, Route } from 'react-router-dom';
import RouteAuth from '@/components/auth/RouteAuth';
import ProtectedApp from "./ProtectedApp";
import ConfirmProvider from './components/ui/custom/ConfirmDialog';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { addNotification } = useStore();
  const subscriptionAmount = useStore((state) => state.subscriptionAmount);


  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      document.documentElement.classList.add('safari');
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      addNotification({
        title: 'Connected',
        message: 'Your internet connection has been restored',
        type: 'success'
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      addNotification({
        title: 'Offline',
        message: 'You are currently offline. Some features may be unavailable.',
        type: 'warning'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [addNotification]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsLoading(true);
      if (!firebaseUser && isOnline) {
        useStore.setState({ user: null, recipes: [] });
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [isOnline]);


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-secondary border-t-transparent"></div>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-4 font-sans">
        <div className="max-w-md w-full bg-white dark:bg-gray-950 rounded-lg shadow-lg p-8 text-center">
          <WifiOff size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
            You're Offline
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Please check your internet connection to access Ezyvibes.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-secondary-800 text-white rounded-lg hover:bg-secondary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <ConfirmProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Routes>
          {/* Public Auth Routes with AuthLayout */}
          <Route
            path="/login"
            element={
              <Login />
            }
          />
          <Route
            path="/signup"
            element={
              <SignUp />
            }
          />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <RouteAuth>
                <ProtectedApp />
              </RouteAuth>
            }
          />
        </Routes>
      </TooltipProvider>
    </ConfirmProvider>
  );
}

export default App;